"use client";

import Link from "next/link";
import { useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react";
import { SanityMark } from "@/components/sanity-mark";
import { AUTH_CYCLE_MS } from "@/mos-app/studio-boot-frame-script";
import { playUiSound } from "@/mos-app/sounds.js";
import { randomizeStudioAppearance } from "@/mos-app/theme.js";
import {
  readStudioCustomCursorEnabled,
  useStudioCustomCursor,
} from "@/studio/hooks/useStudioCustomCursor";
import { nativeImpact } from "@/studio/lib/nativeShell";
import "@/studio/components/studio-auth-sheet.css";
import "./not-found.css";

export function StudioNotFoundClient() {
  const [cycleDelay] = useState(() => -(Date.now() % AUTH_CYCLE_MS));
  const [customCursorEnabled] = useState(readStudioCustomCursorEnabled);
  useStudioCustomCursor(customCursorEnabled);

  const onStagePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const target = event.target as Element | null;
    if (!target || target.closest(".studio-auth-card")) return;

    playUiSound("shuffle");
    void nativeImpact("light");
    randomizeStudioAppearance();
  };

  return (
    <div
      className={`ys-auth-stage studio-not-found-root${customCursorEnabled ? " is-custom-cursor" : ""}`}
      style={{ "--ysbf-cycle-delay": `${cycleDelay}ms` } as CSSProperties}
      data-ys-boot="not-found"
      onPointerDown={onStagePointerDown}
    >
      <main className="studio-auth-theme is-live-theme ysbf-auth-slot studio-not-found-main">
        <section className="studio-auth-card studio-not-found-card">
          <div className="flex flex-col items-center justify-center">
            <SanityMark height={34} className="studio-auth-sanity-mark" title="Sanity" />
          </div>
          <p className="studio-not-found-code">404</p>
          <h1 className="studio-auth-title studio-not-found-title">This page isn&apos;t here</h1>
          <p className="studio-not-found-hint">
            The link may be broken, or the page may have moved.
          </p>
          <div className="studio-auth-card-body studio-not-found-actions">
            <Link href="/" className="studio-auth-primary">
              Go to Yatishara Studio
            </Link>
            <p className="studio-not-found-shuffle">Tap the background to shuffle the theme</p>
          </div>
        </section>
      </main>
    </div>
  );
}
